import { Box, Group, SimpleGrid, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import InsightsContributionHeatmap from './InsightsContributionHeatmap';
import InsightsModelMixChart from './InsightsModelMixChart';
import type { ActivityPulse } from './InsightsTypes';

interface InsightsActivitySectionProps {
    activity: ActivityPulse;
    periodDays: number;
    periodStartIso: string;
    periodEndIso: string;
}

function CardTitle({ children }: { children: string }): ReactElement {
    return (
        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="sm" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
            {children}
        </Text>
    );
}

/** Activity & Engagement — behavioral velocity: when, how much, and with which models. */
export default function InsightsActivitySection({ activity, periodDays, periodStartIso, periodEndIso }: InsightsActivitySectionProps): ReactElement {
    const inputShare = Math.round((activity.inputTokens / Math.max(1, activity.totalTokens)) * 100);
    return (
        <Box>
            <StatBand
                stats={[
                    { label: 'Total tokens', value: formatCompact(activity.totalTokens), hint: `${inputShare}% input / ${100 - inputShare}% output` },
                    { label: 'Active days', value: `${activity.activeDays} of ${periodDays}` },
                    { label: 'Sessions', value: formatCompact(activity.sessions) },
                    {
                        label: 'Avg prompts / session',
                        value: activity.avgTurnsPerSession.toFixed(1),
                        ...(activity.teamAvgTurnsPerSession > 0 ? { hint: `team avg ${activity.teamAvgTurnsPerSession.toFixed(1)}` } : {}),
                    },
                    { label: 'Peak window', value: activity.peakWindow },
                ]}
            />
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" mt="md">
                <SurfaceCard $padding="md">
                    <CardTitle>Session contributions</CardTitle>
                    <Box my="auto">
                        <InsightsContributionHeatmap days={activity.heatmap} accent="teal" startIso={periodStartIso} endIso={periodEndIso} />
                    </Box>
                    <Group gap={6} mt="sm" justify="flex-end" align="center">
                        <Text size="xs" c="dimmed" style={{ fontSize: '9px' }}>
                            Less
                        </Text>
                        {[0.14, 0.35, 0.55, 0.9].map((alpha) => (
                            <Box key={alpha} w={10} h={10} style={{ background: `color-mix(in srgb, var(--bm-color-brand-accent) ${Math.round(alpha * 100)}%, transparent)`, borderRadius: 2 }} />
                        ))}
                        <Text size="xs" c="dimmed" style={{ fontSize: '9px' }}>
                            More
                        </Text>
                    </Group>
                </SurfaceCard>
                <SurfaceCard $padding="md">
                    <CardTitle>Token volume by model</CardTitle>
                    <InsightsModelMixChart models={activity.models} points={activity.modelMix} startIso={periodStartIso} endIso={periodEndIso} />
                </SurfaceCard>
            </SimpleGrid>
        </Box>
    );
}
